import React, { useEffect, useState, useCallback } from "react";
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  syncContacts,
  type ContactDTO,
} from "../lib/api";
import {
  Search,
  Plus,
  RefreshCw,
  Phone,
  Mail,
  User,
  Building2,
  Briefcase,
  MessageSquare,
  Trash2,
  Edit3,
  X,
  Check,
  Users,
  Loader,
  Tag,
} from "lucide-react";

/* ═══════════════════════════════════════════════ */
/*              Badge de source                   */
/* ═══════════════════════════════════════════════ */
function SourceBadge({ source }: { source: string }) {
  const map: Record<string, { bg: string; label: string }> = {
    system:   { bg: "bg-blue-500/20 text-blue-300 border-blue-500/30",    label: "Equipo" },
    org:      { bg: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "Organización" },
    personal: { bg: "bg-purple-500/20 text-purple-300 border-purple-500/30",    label: "Personal" },
  };
  const s = map[source] || map.org;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.bg}`}>
      {s.label}
    </span>
  );
}

/* ═══════════════════════════════════════════════ */
/*              Avatar                             */
/* ═══════════════════════════════════════════════ */
function Avatar({ nombre, foto }: { nombre: string; foto?: string | null }) {
  if (foto) {
    return (
      <img src={foto} alt={nombre} className="w-10 h-10 rounded-full object-cover border border-white/10" />
    );
  }
  const initial = nombre?.charAt(0)?.toUpperCase() || "?";
  const colors = [
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-teal-500",
    "from-purple-500 to-pink-500",
    "from-amber-500 to-orange-500",
    "from-rose-500 to-red-500",
  ];
  const idx = nombre.charCodeAt(0) % colors.length;
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center text-white font-bold text-sm`}>
      {initial}
    </div>
  );
}

/* ═══════════════════════════════════════════════ */
/*         Modal para crear/editar                 */
/* ═══════════════════════════════════════════════ */
type ModalData = Partial<ContactDTO> & { _editing?: boolean };

function ContactModal({
  data,
  onClose,
  onSave,
}: {
  data: ModalData;
  onClose: () => void;
  onSave: (d: ModalData) => void;
}) {
  const [form, setForm] = useState<ModalData>({ ...data, source: data.source || "org" });
  const [saving, setSaving] = useState(false);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white">
            {data._editing ? "Editar Contacto" : "Nuevo Contacto"}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          {/* Nombre */}
          <div className="grid grid-cols-2 gap-3">
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Nombre *" value={form.nombre || ""} onChange={(e) => set("nombre", e.target.value)} />
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Apellido" value={form.apellido || ""} onChange={(e) => set("apellido", e.target.value)} />
          </div>
          {/* Email & Teléfono */}
          <div className="grid grid-cols-2 gap-3">
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Teléfono" value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} />
          </div>
          {/* Puesto & Departamento */}
          <div className="grid grid-cols-2 gap-3">
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Puesto / Cargo" value={form.puesto || ""} onChange={(e) => set("puesto", e.target.value)} />
            <input className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none" placeholder="Departamento" value={form.departamento || ""} onChange={(e) => set("departamento", e.target.value)} />
          </div>
          {/* Canal preferido */}
          <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 outline-none" value={form.canal_preferido || "whatsapp"} onChange={(e) => set("canal_preferido", e.target.value)}>
            <option value="whatsapp">📱 WhatsApp</option>
            <option value="email">📧 Email</option>
            <option value="ambos">📱📧 Ambos</option>
          </select>
          {/* Tipo */}
          {!data._editing && (
            <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 outline-none" value={form.source || "org"} onChange={(e) => set("source", e.target.value)}>
              <option value="org">🏢 Organización (visible para todos)</option>
              <option value="personal">👤 Personal (solo para mí)</option>
            </select>
          )}
          {/* Notas */}
          <textarea className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none resize-none" rows={2} placeholder="Notas..." value={form.notas || ""} onChange={(e) => set("notas", e.target.value)} />
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving || !form.nombre} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
            {data._editing ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════ */
/*        Componente principal: ContactsPage       */
/* ═══════════════════════════════════════════════ */
export function ContactsPage() {
  const [contacts, setContacts] = useState<ContactDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState<ModalData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContacts(search || undefined, sourceFilter || undefined);
      setContacts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error cargando contactos:", e);
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncContacts();
      alert(`✅ ${result.message}`);
      load();
    } catch (e: any) {
      alert("Error sincronizando: " + (e?.message || ""));
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (data: ModalData) => {
    try {
      if (data._editing && data.id) {
        await updateContact(data.id, data);
      } else {
        await createContact(data);
      }
      setModal(null);
      load();
    } catch (e: any) {
      alert("Error: " + (e?.message || ""));
    }
  };

  const handleDelete = async (c: ContactDTO) => {
    if (!confirm(`¿Eliminar a ${c.nombre} ${c.apellido || ""}?`)) return;
    try {
      await deleteContact(c.id);
      load();
    } catch (e: any) {
      alert("Error: " + (e?.message || ""));
    }
  };

  // Stats
  const total = contacts.length;
  const teamCount = contacts.filter((c) => c.source === "system").length;
  const orgCount = contacts.filter((c) => c.source === "org").length;
  const personalCount = contacts.filter((c) => c.source === "personal").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-cyan-400" />
          <h1 className="text-xl font-bold text-white">Directorio de Contactos</h1>
          <span className="text-xs text-white/40 bg-white/5 px-2 py-1 rounded-full">{total} contactos</span>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-2 px-3 py-2 bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/30 text-blue-300 rounded-lg text-xs font-medium disabled:opacity-50">
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            Sincronizar Equipo
          </button>
          <button onClick={() => setModal({})} className="flex items-center gap-2 px-3 py-2 bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-500/30 text-cyan-300 rounded-lg text-xs font-medium">
            <Plus size={14} />
            Agregar Contacto
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: total, color: "text-white" },
          { label: "Equipo", value: teamCount, color: "text-blue-400" },
          { label: "Organización", value: orgCount, color: "text-emerald-400" },
          { label: "Personales", value: personalCount, color: "text-purple-400" },
        ].map((s) => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[11px] text-white/40">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/30 focus:border-cyan-500 outline-none"
            placeholder="Buscar por nombre, email, teléfono, puesto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 outline-none" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
          <option value="">Todos</option>
          <option value="system">🔵 Equipo</option>
          <option value="org">🟢 Organización</option>
          <option value="personal">🟣 Personales</option>
        </select>
      </div>

      {/* Contact list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="animate-spin text-cyan-400" size={28} />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-white/40">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay contactos todavía.</p>
          <p className="text-xs mt-1">Usa "Sincronizar Equipo" para importar admins y devs, o agrega uno manualmente.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {contacts.map((c) => (
            <div
              key={c.id}
              className="group bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-white/10 rounded-xl px-4 py-3 flex items-center gap-4 transition-all"
            >
              <Avatar nombre={c.nombre} foto={c.foto} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm truncate">
                    {c.nombre} {c.apellido || ""}
                  </span>
                  <SourceBadge source={c.source} />
                  {c.tags?.map((t) => (
                    <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/5">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                  {c.puesto && (
                    <span className="flex items-center gap-1">
                      <Briefcase size={10} /> {c.puesto}
                    </span>
                  )}
                  {c.departamento && (
                    <span className="flex items-center gap-1">
                      <Building2 size={10} /> {c.departamento}
                    </span>
                  )}
                </div>
              </div>

              {/* Contact info */}
              <div className="hidden sm:flex items-center gap-4 text-xs text-white/50">
                {c.telefono && (
                  <a href={`https://wa.me/52${c.telefono}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-green-400 transition-colors" title="WhatsApp">
                    <Phone size={12} /> {c.telefono}
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-cyan-400 transition-colors" title="Email">
                    <Mail size={12} /> {c.email}
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                {c.source !== "system" && (
                  <>
                    <button onClick={() => setModal({ ...c, _editing: true })} className="p-1.5 hover:bg-white/10 rounded-lg text-white/40 hover:text-white" title="Editar">
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => handleDelete(c)} className="p-1.5 hover:bg-rose-500/20 rounded-lg text-white/40 hover:text-rose-400" title="Eliminar">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && <ContactModal data={modal} onClose={() => setModal(null)} onSave={handleSave} />}
    </div>
  );
}

export default ContactsPage;
